import { WhiteLoadingIndicator } from "../UI/Loading/WhiteLoadingIndicator.tsx";
import { FullPageLoading } from "../UI/Loading/FullPageLoading.tsx";
import { Layout } from "../UI/Layout/Layout.tsx";
import { formatDateTime } from "../UI/Utils/formatDate.ts";
import { Card } from "../UI/Card.tsx";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { BreadCrumbsWrapper } from "../UI/Layout/BreadCrumbsWrapper.tsx";
import { BreadCrumbsItem } from "../UI/Layout/BreadCrumbsItem.tsx";
import { useParams } from "react-router-dom";
import { useGetBulkClassRoomCreationRequest } from "./useGetBulkClassRoomCreationRequest.ts";
import { ErrorFeedback } from "../UI/Feedback/ErrorFeedback.tsx";
import { BulkClassRoomCreationRequestStatus } from "../gql/graphql.ts";

export const BulkClassRoomCreationRequestDetailScreenPath =
  "/bulk-classrooms/request/:id";

export function BulkClassRoomCreationRequestDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const { request, loading, error } = useGetBulkClassRoomCreationRequest(
    id ?? "",
  );

  if (loading && !request) {
    return <FullPageLoading />;
  }

  if (error || !request) {
    return (
      <Layout>
        <Card>
          <ErrorFeedback
            title={"Errore nel caricamento della richiesta"}
            message={error?.message ?? "Richiesta non trovata"}
          />
        </Card>
      </Layout>
    );
  }

  const getStatusBadge = (status: BulkClassRoomCreationRequestStatus) => {
    if (status === BulkClassRoomCreationRequestStatus.Completed) {
      return (
        <span className="px-3 py-1 bg-green-600 text-white rounded-md text-sm font-medium">
          Completato
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-600 text-white rounded-md text-sm font-medium">
        In Creazione...
        {loading && <WhiteLoadingIndicator />}
      </span>
    );
  };

  return (
    <Layout>
      <Card>
        <ScreenTitleBlock
          title={"Dettaglio Richiesta Creazione Bulk ClassRoom"}
          breadCrumbs={
            <BreadCrumbsWrapper>
              <BreadCrumbsItem title={"Dettaglio Richiesta"} />
            </BreadCrumbsWrapper>
          }
        />
      </Card>

      <div className={"mt-4"}>
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Stato della Richiesta</h2>
              {getStatusBadge(request.status)}
            </div>

            {request.status === BulkClassRoomCreationRequestStatus.Creating && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  ℹ️ La creazione delle classi è in corso. La pagina si
                  aggiornerà automaticamente ogni 15 secondi.
                </p>
              </div>
            )}

            {request.status ===
              BulkClassRoomCreationRequestStatus.Completed && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800">
                  ✓ Tutte le classi sono state create con successo!
                </p>
              </div>
            )}

            <div className="space-y-3 text-sm">
              <div className="p-4 bg-gray-50 rounded-md">
                <h3 className="font-semibold mb-2">Insegnante</h3>
                <p>
                  {request.teacher.givenName} {request.teacher.familyName}
                </p>
              </div>

              <div className="p-4 bg-gray-50 rounded-md">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold mb-2">Studente</h3>
                    <p>
                      {request.user.givenName} {request.user.familyName}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      window.open(
                        `${import.meta.env.VITE_USER_APP_BASE_URL}/impersonate-user?uid=${request.user.authId}`,
                        "_blank",
                        "noopener noreferrer",
                      )
                    }
                    className="px-4 py-2 text-purple-600 hover:text-purple-900 font-medium border border-purple-600 hover:border-purple-900 rounded-md transition-colors"
                    title="Impersona utente"
                  >
                    Impersona
                  </button>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-md">
                <h3 className="font-semibold mb-2">
                  Numero totale di classi da creare
                </h3>
                <p className="text-2xl font-bold text-blue-600">
                  {request.totalClassRoomsToCreate}
                </p>
              </div>

              <div className="p-4 bg-gray-50 rounded-md">
                <h3 className="font-semibold mb-2">Creata il</h3>
                <p>{formatDateTime(request.createdAt, { locale: "it" })}</p>
              </div>

              {request.updatedAt && (
                <div className="p-4 bg-gray-50 rounded-md">
                  <h3 className="font-semibold mb-2">Ultimo aggiornamento</h3>
                  <p>{formatDateTime(request.updatedAt, { locale: "it" })}</p>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
